import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Filter, Trash2, History, TrendingDown, Sparkles, LayoutGrid } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DashboardLayout from '../components/DashboardLayout';
import { expenseAPI } from '../services/apiServices';
import { useCachedApi } from '../hooks/useCachedApi';
import { useCache } from '../contexts/CacheContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';

const allCategories = [
    'Food', 'Transport', 'Shopping', 'Bills',
    'Entertainment', 'Health', 'Education',
    'Groceries', 'Rent', 'Utilities', 'Salary',
    'Investment', 'Fuel', 'EMI', 'Travel', 'Others'
];

const Switch = ({ checked, onCheckedChange }) => (
    <button
        type="button"
        onClick={() => onCheckedChange(!checked)}
        className={`w-8 h-4 rounded-full relative transition-colors ${checked ? 'bg-purple-500' : 'bg-slate-700'}`}
    >
        <span className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
    </button>
);

const ExpenseHistory = () => {
    const navigate = useNavigate();
    const { invalidateCache } = useCache();

    // State
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [showOnlyRecurring, setShowOnlyRecurring] = useState(false);

    const {
        data: allExpenses = [],
        loading,
        refetch
    } = useCachedApi(expenseAPI.getExpenses, 'expenses_full', {
        onError: () => toast({ title: 'Error', description: 'Failed to load history.' })
    });

    const getCategoryColor = (cat) => {
        const colors = {
            Food: '#A855F7', Transport: '#8B5CF6', Shopping: '#D946EF', Bills: '#C084FC',
            Entertainment: '#9333EA', Education: '#7C3AED', Health: '#6D28D9',
            Groceries: '#5B21B6', Rent: '#4C1D95', Utilities: '#8B5CF6', Others: '#6B7280'
        };
        return colors[cat] || '#8B5CF6';
    };

    const filteredExpenses = allExpenses.filter(exp => {
        const matchesSearch = exp.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            exp.category.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategory === 'all' || exp.category === filterCategory;
        const matchesRecurring = !showOnlyRecurring || exp.source === 'Recurring' || exp.is_recurring === true; // Handle both flag styles

        let matchesDate = true;
        if (dateRange.start) matchesDate = matchesDate && new Date(exp.date) >= new Date(dateRange.start);
        if (dateRange.end) matchesDate = matchesDate && new Date(exp.date) <= new Date(dateRange.end);

        return matchesSearch && matchesCategory && matchesDate && matchesRecurring;
    });

    // Grouping logic
    const groupedExpenses = filteredExpenses.reduce((groups, expense) => {
        const date = new Date(expense.date);
        const monthYear = date.toLocaleString('default', { month: 'long', year: 'numeric' });
        if (!groups[monthYear]) groups[monthYear] = [];
        groups[monthYear].push(expense);
        return groups;
    }, {});

    const handleDeleteExpense = async (id) => {
        try {
            await expenseAPI.deleteExpense(id);
            toast({ title: 'Success', description: 'Expense deleted.' });
            invalidateCache('expenses_full');
            invalidateCache('expenses');
            refetch();
        } catch (e) {
            toast({ title: 'Error', description: 'Failed to delete.', variant: 'destructive' });
        }
    };

    const totalHistoryAmount = filteredExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);

    return (
        <DashboardLayout>
            <div className="min-h-screen bg-[#020617] text-slate-200 p-6 md:p-10 font-jakarta overflow-x-hidden">
                <style>{`
                    @keyframes purple-glow {
                        0%, 100% { box-shadow: 0 0 20px rgba(168, 85, 247, 0.1), inset 0 0 10px rgba(168, 85, 247, 0.05); }
                        50% { box-shadow: 0 0 30px rgba(168, 85, 247, 0.2), inset 0 0 15px rgba(168, 85, 247, 0.1); }
                    }
                    .purple-glass-card {
                        background: rgba(15, 23, 42, 0.6);
                        backdrop-filter: blur(16px);
                        border: 1px solid rgba(168, 85, 247, 0.15);
                        animation: purple-glow 4s ease-in-out infinite;
                    }
                    .month-divider {
                        position: sticky;
                        top: 0;
                        z-index: 20;
                        background: rgba(2, 6, 23, 0.8);
                        backdrop-filter: blur(8px);
                    }
                `}</style>

                {/* Back Link */}
                <button
                    onClick={() => navigate('/expenses')}
                    className="flex items-center gap-2 text-slate-500 hover:text-purple-400 transition-colors mb-8 group"
                >
                    <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                    <span className="text-xs font-black uppercase tracking-[0.2em]">Return to Tracker</span>
                </button>

                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
                    <div>
                        <div className="mb-3 flex items-center gap-2 bg-purple-500/10 px-2 py-0.5 rounded-full border border-purple-500/20 w-fit">
                            <History size={10} className="text-purple-400" />
                            <span className="text-[8px] font-black uppercase tracking-[0.3em] text-purple-400">Archival Intelligence</span>
                        </div>
                        <h1 className="text-xl md:text-3xl font-black tracking-tighter text-white uppercase leading-none">
                            EXPENSE <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-fuchsia-500">HISTORY</span>
                        </h1>
                        <p className="text-slate-500 text-sm mt-4 max-w-xl font-medium leading-relaxed">
                            Chronological breakdown of all fiscal transactions processed through the FinAssure ecosystem.
                        </p>
                    </div>

                    <div className="purple-glass-card px-8 py-6 rounded-[2rem] text-right">
                        <p className="text-slate-400 text-xs font-black uppercase tracking-widest mb-1">Lifetime Expenditure</p>
                        <p className="text-white text-2xl font-black">₹{Number(totalHistoryAmount).toLocaleString('en-IN')}</p>
                    </div>
                </div>

                {/* Filter Sub-Bar */}
                <div className="purple-glass-card p-4 rounded-3xl mb-10 flex flex-wrap items-center gap-6 animate-in fade-in zoom-in-95 duration-500">
                    <div className="flex-1 min-w-[200px] relative">
                        <LayoutGrid className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                        <Input
                            placeholder="Find transaction..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="bg-slate-900/50 border-purple-500/10 h-11 pl-10 rounded-2xl text-sm focus:ring-purple-500/20 focus:border-purple-500/30 w-full"
                        />
                    </div>

                    <div className="flex items-center gap-4 flex-wrap">
                        {/* Category Select */}
                        <Select value={filterCategory} onValueChange={setFilterCategory}>
                            <SelectTrigger className="w-40 bg-slate-900/50 border-purple-500/10 h-11 rounded-2xl text-xs text-slate-300">
                                <Filter size={14} className="mr-2 text-purple-400" />
                                <SelectValue placeholder="Category" />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-900 border-purple-500/20 text-slate-200">
                                <SelectItem value="all">Every Category</SelectItem>
                                {allCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>

                        {/* Recurring Toggle */}
                        <div className="flex items-center gap-3 bg-purple-500/5 px-4 h-11 rounded-2xl border border-purple-500/10">
                            <span className="text-[10px] font-black text-purple-400/60 uppercase tracking-widest">Only Recurring</span>
                            <Switch checked={showOnlyRecurring} onCheckedChange={setShowOnlyRecurring} />
                        </div>

                        {/* Date Range Group */}
                        <div className="flex items-center gap-3 bg-black/40 px-4 h-11 rounded-2xl border border-white/5">
                            <Calendar size={14} className="text-purple-500/40" />
                            <Input
                                type="date"
                                value={dateRange.start}
                                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                                className="h-9 w-32 bg-transparent border-none text-[10px] text-white p-0"
                            />
                            <div className="w-2 h-px bg-slate-700"></div>
                            <Input
                                type="date"
                                value={dateRange.end}
                                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                                className="h-9 w-32 bg-transparent border-none text-[10px] text-white p-0"
                            />
                            {(dateRange.start || dateRange.end) && (
                                <button
                                    onClick={() => setDateRange({ start: '', end: '' })}
                                    className="p-1 hover:bg-white/10 rounded-full text-slate-400 transition-colors"
                                >
                                    <Trash2 size={12} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Transactions Scroll */}
                <div className="space-y-12 pb-20">
                    {Object.keys(groupedExpenses).length > 0 ? (
                        Object.keys(groupedExpenses).sort((a, b) => new Date(b) - new Date(a)).map((monthYear) => (
                            <div key={monthYear} className="relative">
                                {/* Month Sticky Header */}
                                <div className="month-divider mb-6 py-2 border-b border-purple-500/10 flex items-center justify-between">
                                    <h2 className="text-xl font-black text-white uppercase tracking-tighter">{monthYear}</h2>
                                    <span className="text-[10px] font-black bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full border border-purple-500/30">
                                        {groupedExpenses[monthYear].length} TRANSACTIONS
                                    </span>
                                </div>

                                <div className="grid gap-4">
                                    {groupedExpenses[monthYear].map((exp, idx) => {
                                        const color = getCategoryColor(exp.category);
                                        return (
                                            <div
                                                key={exp.id}
                                                className="purple-glass-card group p-5 rounded-2xl flex items-center justify-between hover:scale-[1.01] transition-all duration-300"
                                            >
                                                <div className="flex items-center gap-5">
                                                    <div
                                                        className="w-12 h-12 rounded-2xl flex items-center justify-center text-xl shadow-lg transition-transform group-hover:scale-110"
                                                        style={{ backgroundColor: `${color}15`, color: color, border: `1px solid ${color}30` }}
                                                    >
                                                        {exp.category === 'Food' ? '🍔' : exp.category === 'Transport' ? '🚕' :
                                                            exp.category === 'Shopping' ? '🛍️' : exp.category === 'Bills' ? '🧾' : '📦'}
                                                    </div>
                                                    <div>
                                                        <h3 className="text-white font-bold text-base group-hover:text-purple-300 transition-colors uppercase tracking-tight">{exp.description}</h3>
                                                        <div className="flex items-center gap-3 mt-1">
                                                            <span className="text-[10px] text-slate-500 font-mono">{new Date(exp.date).toLocaleDateString()}</span>
                                                            <span className="w-1 h-1 bg-slate-800 rounded-full"></span>
                                                            <span className="text-[10px] text-purple-400/60 font-black uppercase tracking-widest">{exp.category}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-8">
                                                    <div className="text-right">
                                                        <p className="text-white font-black text-xl tracking-tighter transition-all group-hover:text-purple-400">
                                                            -₹{Number(exp.amount).toLocaleString('en-IN')}
                                                        </p>
                                                        <p className="text-[8px] text-slate-600 font-black uppercase tracking-[0.2em] mt-0.5">Final Settlement</p>
                                                    </div>
                                                    <button
                                                        onClick={() => handleDeleteExpense(exp.id)}
                                                        className="p-3 rounded-full bg-red-500/5 text-red-500/20 hover:text-red-500 hover:bg-red-500/10 transition-all opacity-0 group-hover:opacity-100"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="py-40 text-center flex flex-col items-center gap-4">
                            <div className="w-20 h-20 rounded-full bg-slate-900 flex items-center justify-center border border-dashed border-slate-700">
                                <History size={40} className="text-slate-700" />
                            </div>
                            <p className="text-slate-500 font-medium italic">No historical data found matching criteria.</p>
                        </div>
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
};

export default ExpenseHistory;
